import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReceiptText, ArrowLeft, Download, CreditCard } from 'lucide-react';
import { memberPortal, useAsync, openAuthedPdf } from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface InvoiceRow {
  id: string; invoice_number: string; status: string;
  issue_date?: string; due_date?: string;
  total: string; paid_amount?: string; currency?: string;
  is_credit_note?: boolean;
}

function money(v: string, cur = 'MUR') {
  const n = Number(v ?? 0);
  return `${cur} ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const STATUS: Record<string, 'success' | 'warning' | 'destructive' | 'outline' | 'secondary'> = {
  paid: 'success', overdue: 'destructive', partially_paid: 'warning',
  sent: 'secondary', draft: 'outline', cancelled: 'outline', refunded: 'outline',
};

export default function Invoices() {
  const { data, loading } = useAsync(() => memberPortal.invoices() as Promise<InvoiceRow[]>);
  const rows = Array.isArray(data) ? data : [];
  const [payingId, setPayingId] = useState<string | null>(null);

  function isPayable(inv: InvoiceRow): boolean {
    if (inv.is_credit_note) return false;
    const outstanding = Number(inv.total || 0) - Number(inv.paid_amount || 0);
    return outstanding > 0
      && ['sent', 'viewed', 'overdue', 'partially_paid'].includes(inv.status);
  }

  async function pay(inv: InvoiceRow) {
    setPayingId(inv.id);
    try {
      const r = await memberPortal.payInvoice(inv.id, {
        provider: 'mips',
        return_url: location.origin + '/invoices?paid=1',
        cancel_url: location.origin + '/invoices?cancelled=1',
      }) as { redirect_url?: string };
      if (r.redirect_url) location.href = r.redirect_url;
      else window.alert('Payment session started but no redirect URL returned.');
    } catch (e) { window.alert(`Could not start payment: ${(e as Error).message}`); }
    finally { setPayingId(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/me"><ArrowLeft className="h-4 w-4 mr-1" />My account</Link>
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">Invoices</h1>
      </div>

      {loading ? <Skeleton className="h-24 w-full" /> :
        rows.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            <ReceiptText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
            No invoices yet.
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map((inv) => (
              <Card key={inv.id} className="p-3">
                <div className="flex items-center gap-3">
                  <ReceiptText className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-medium text-sm">
                      {inv.invoice_number}
                      {inv.is_credit_note && <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Credit note</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : '—'}
                      {inv.due_date && !inv.is_credit_note ? ` · due ${new Date(inv.due_date).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <Badge variant={STATUS[inv.status] || 'outline'} className="capitalize">
                    {inv.status.replace('_', ' ')}
                  </Badge>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums text-sm">{money(inv.total, inv.currency || 'MUR')}</div>
                  </div>
                  {isPayable(inv) && (
                    <Button size="sm" onClick={() => void pay(inv)} disabled={payingId === inv.id}>
                      <CreditCard className="h-3.5 w-3.5 mr-1" />
                      {payingId === inv.id ? 'Starting…' : 'Pay'}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" title="Download PDF"
                    onClick={() => void openAuthedPdf(memberPortal.invoicePdfUrl(inv.id, true),
                      { mode: 'download', filename: `invoice-${inv.invoice_number}.pdf`, asMember: true })
                      .catch((e: Error) => window.alert(`Download failed: ${e.message}`))}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
